import type {
  BadAddressReasonItem,
  FaqItem,
  HelpDocument,
  HygieneFactor,
  LookupItem,
  ReasonItem,
  Reasons,
  RelationshipGroups,
  RelationshipItem,
} from '../../models/reference.model';
import type { AppSettings } from '../../models/settings.model';
import packageInfo from '../../../../../package.json';
import { resolveList, unwrapData } from './adapter.utils';

// Confirmed field names from live API (2026-06-05, updated 2026-07-28 for MOB-278):
//   observation:           { data: [{ observation_id, observation_name }] }
//   event-type:             { data: [{ event_id, event_name, event_description }] }
//   completion-type:        { data: [{ completiontype_id, completiontype_name }] }
//   bad-address-reason (v2 only): { data: [{ badaddressreason_id, badaddressreason_name, badaddressreason_template }] }
//     — v1 omits `badaddressreason_template` entirely; use badAddressReasonToModel(), not toModel().
//   relationship (v2):      { data: { spokewith_relationship: [{relationship_id, relationship_name, relationship_phrase}],
//                                       leftwith_relationship: [{relationship_id, relationship_name, relationship_phrase}] } }
// The id field ends in `_id`; the display name field ends in `_name`.
// `hygiene-factor` and `reason` use keyed-object shapes — use their dedicated adapters.

/**
 * Extracts the id value from a raw reference item.
 * Checks for a literal `id` field first, then falls back to the first `*_id` key.
 */
function extractId(item: Record<string, unknown>): string {
  if (item['id'] !== undefined) return String(item['id']);
  const key = Object.keys(item).find((k) => k.endsWith('_id'));
  return key !== undefined ? String(item[key]) : '';
}

/**
 * Extracts the display label from a raw reference item.
 * Checks common label synonyms first, then falls back to the first `*_name` key.
 */
function extractLabel(item: Record<string, unknown>): string {
  for (const field of ['name', 'label', 'title'] as const) {
    if (typeof item[field] === 'string') return item[field] as string;
  }
  const key = Object.keys(item).find((k) => k.endsWith('_name'));
  return key !== undefined && typeof item[key] === 'string' ? (item[key] as string) : '';
}

/**
 * Maps a flat reference-data endpoint response to an array of {@link LookupItem}.
 *
 * @remarks
 * Handles both `{ data: [...] }` envelope and bare array responses.
 * Supports any `*_id` / `*_name` field name convention used by the API
 * (e.g. `observation_id` / `observation_name`, `event_id` / `event_name`).
 *
 * For nested endpoints (spoke-with, left-with, reason, hygiene-factor) that do NOT
 * return a flat array, use the dedicated typed adapters in this module instead.
 *
 * @param raw - Response from observation, event-type, completion-type, or bad-address-reason.
 */
export function toModel(raw: unknown): LookupItem[] {
  const list = resolveList<Record<string, unknown>>(raw);
  return list.map((item) => ({
    id: extractId(item),
    label: extractLabel(item),
    active: true,
  }));
}

/**
 * Maps the `GET /relationship` (v2) response to {@link RelationshipGroups}.
 *
 * @remarks
 * Replaces the v1 `spoke-with-relationship` / `left-with-relationship`
 * endpoints (which nested each relationship list inside per-completion-type
 * groups requiring de-duplication) with a single call whose two arrays are
 * already flat, so each is mapped via {@link toRelationshipItems} once the
 * outer `data` envelope is unwrapped. Not built on the generic {@link toModel}
 * — this endpoint's items additionally carry `relationship_phrase` (MOB-278),
 * which the generic `LookupItem` shape (shared by `observation`/`event-type`/
 * `completion-type`) must not gain.
 *
 * @param raw - Response from `GET /relationship`.
 */
export function relationshipToModel(raw: unknown): RelationshipGroups {
  const data = unwrapData(raw);
  return {
    spokeWith: toRelationshipItems(data['spokewith_relationship'] ?? []),
    leftWith: toRelationshipItems(data['leftwith_relationship'] ?? []),
  };
}

/** Maps a flat `GET /relationship` category array to {@link RelationshipItem}[], including `relationship_phrase`. */
function toRelationshipItems(raw: unknown): RelationshipItem[] {
  const list = resolveList<Record<string, unknown>>(raw);
  return list.map((item) => ({
    id: extractId(item),
    label: extractLabel(item),
    active: true,
    phrase: typeof item['relationship_phrase'] === 'string' ? item['relationship_phrase'] : '',
  }));
}

/**
 * Maps the `GET /bad-address-reason` (v2) response to {@link BadAddressReasonItem}[],
 * including each reason's optional note-generation `template`.
 *
 * @remarks
 * A dedicated adapter (not the generic {@link toModel}) because `observation`,
 * `event-type`, and `completion-type` share `toModel()`'s plain {@link LookupItem}
 * shape and must not gain an unused `template` field. `template` is `''` when
 * absent (v1's response never includes `badaddressreason_template` at all).
 *
 * @param raw - Response from `GET /bad-address-reason` (v2).
 */
export function badAddressReasonToModel(raw: unknown): BadAddressReasonItem[] {
  const list = resolveList<Record<string, unknown>>(raw);
  return list.map((item) => ({
    id: extractId(item),
    label: extractLabel(item),
    active: true,
    template: typeof item['badaddressreason_template'] === 'string' ? item['badaddressreason_template'] : '',
  }));
}

/**
 * Maps the raw `GET /faqs` response to an array of {@link FaqItem}.
 *
 * @remarks
 * Live API returns `{ data: [{ faq_question, faq_answer }] }`.
 *
 * @param raw - Untyped API response.
 */
export function faqsToModel(raw: unknown): FaqItem[] {
  return resolveList<Record<string, unknown>>(raw).map((item) => ({
    question: (item['faq_question'] as string | undefined) ?? '',
    answer: (item['faq_answer'] as string | undefined) ?? '',
  }));
}

/**
 * Maps the raw `GET /settings/app` response to {@link AppSettings}.
 *
 * @remarks
 * Live API returns a flat object (no `data` wrapper).
 * Maps `mobile_app_refresh_interval_ms` and version fields.
 * Supports both old field names (mobile_app_min_ver, ios_min_ver, android_min_ver)
 * and new names (mobile_app_minimum_version).
 * Platform-specific versions (ios_min_ver, android_min_ver) are extracted and take precedence
 * over generic minimum_version on their respective platforms.
 *
 * **currentVersion:** Always sourced from `package.json` — the installed app version.
 *
 * **reprintThresholdAndroid/Ios/Web:** From `print_packet_threshold_android`/
 * `_ios`/`_web` (2026-07-29 fix — the real API has no single generic
 * `print_packet_reprint_threshold` field; that name was never actually
 * verified against `docs/openapispec.yml`, which instead shows these three
 * platform-specific fields, mirroring the existing `ios_min_ver`/
 * `android_min_ver` platform-specific pattern above). Each is coerced via
 * `Number()` (the API may send it as a string, like other numeric settings
 * fields) and defaults to `1` when absent, non-numeric, or not a positive
 * integer — never `0`/negative, which would disable Print entirely for
 * every job regardless of print count. `PrintQueueComponent` resolves which
 * one applies for the current platform.
 *
 * @param raw - Untyped API response.
 */
export function appSettingsToModel(raw: unknown): AppSettings {
  const obj = (raw ?? {}) as Record<string, unknown>;

  // Resolve minimum version: prefer new field name, fall back to generic
  const minimumVersion = (obj['mobile_app_minimum_version'] as string | undefined) ??
                         (obj['mobile_app_min_ver'] as string | undefined);

  // Current version always from package.json
  const currentVersion = packageInfo.version as string;

  return {
    refreshIntervalMs: (obj['mobile_app_refresh_interval_ms'] as number | undefined) ?? 60_000,
    minimumVersion,
    iosMinVersion: (obj['ios_min_ver'] as string | undefined),
    androidMinVersion: (obj['android_min_ver'] as string | undefined),
    currentVersion,
    reprintThresholdAndroid: parsePositiveIntOrDefault(obj['print_packet_threshold_android']),
    reprintThresholdIos:     parsePositiveIntOrDefault(obj['print_packet_threshold_ios']),
    reprintThresholdWeb:     parsePositiveIntOrDefault(obj['print_packet_threshold_web']),
  };
}

/**
 * Coerces a raw settings value to a positive integer, defaulting to `1`
 * when absent, non-numeric, or not a positive integer.
 *
 * @param raw - Raw value from the settings API response (may be a string).
 */
function parsePositiveIntOrDefault(raw: unknown): number {
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
}

/**
 * Maps the raw `GET /hygiene-factor` response to a {@link HygieneFactor}.
 *
 * @remarks
 * Live API shape: `{ data: { Gender:[], Skin_Color:[], Hair_Color:[], Age:[], Height:[], Weight:[] } }`.
 * Keys are PascalCase/underscore in the API; mapped to camelCase domain fields.
 *
 * @param raw - Untyped API response.
 */
export function hygieneFactorToModel(raw: unknown): HygieneFactor {
  const data = unwrapData(raw);
  return {
    gender: (data['Gender'] as string[]) ?? [],
    skinColor: (data['Skin_Color'] as string[]) ?? [],
    hairColor: (data['Hair_Color'] as string[]) ?? [],
    age: (data['Age'] as number[]) ?? [],
    height: (data['Height'] as string[]) ?? [],
    weight: (data['Weight'] as string[]) ?? [],
  };
}

/**
 * Maps the raw `GET /reason` response to a {@link Reasons} domain object.
 *
 * @remarks
 * Live API shape: `{ data: { reason_for_doe:[...], reason_for_jane_john:[...], reason_for_party_not_served:[...] } }`.
 *
 * @param raw - Untyped API response.
 */
export function reasonToModel(raw: unknown): Reasons {
  const data = unwrapData(raw);
  return {
    forDoe:            mapReasonItems((data['reason_for_doe']              as unknown[]) ?? []),
    forJaneJohn:       mapReasonItems((data['reason_for_jane_john']        as unknown[]) ?? []),
    forPartyNotServed: mapReasonItems((data['reason_for_party_not_served'] as unknown[]) ?? []),
  };
}

function mapReasonItems(arr: unknown[]): ReasonItem[] {
  return arr.map((item) => {
    const r = item as Record<string, unknown>;
    return {
      id: (r['reason_id'] as number) ?? 0,
      name: (r['reason_name'] as string) ?? '',
      category: (r['category'] as string) ?? '',
    };
  });
}

/**
 * Maps the raw `GET /handshake` response to a boolean connectivity flag.
 *
 * @remarks
 * The endpoint returns `{}` when the Bearer token is valid and the server is
 * reachable; any response (including empty object) indicates a successful handshake.
 * Returns `false` only when `raw` is explicitly `null` or `undefined` (i.e. the
 * call itself failed before reaching this adapter).
 *
 * @param raw - Untyped API response (`{}` on success).
 */
export function handshakeToModel(raw: unknown): boolean {
  return raw !== null && raw !== undefined;
}

/**
 * Maps the raw `GET /help-document` response to a {@link HelpDocument}.
 *
 * @remarks
 * Live API shape: `{ data: { document_name, document_version, document_source, document_type } }`.
 * `document_source` is a base64-encoded string of the file content.
 * Requires `document_type` and `released_document_version` query parameters.
 *
 * @param raw - Untyped API response.
 */
export function helpDocumentToModel(raw: unknown): HelpDocument {
  const data = unwrapData(raw);
  return {
    name: (data['document_name'] as string) ?? '',
    version: (data['document_version'] as string) ?? '',
    source: (data['document_source'] as string) ?? '',
    documentType: (data['document_type'] as string) ?? '',
  };
}
